import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Search, ChevronLeft, ChevronRight, Loader, Plus,
  FolderOpen, Inbox, Zap, HelpCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import EventDetailPane from '../components/investigations/EventDetailPane';
import CaseCreationWizard from '../components/investigations/CaseCreationWizard';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import investigationsTutorial from '../components/tutorials/configs/investigationsTutorial';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const PAGE_SIZE = 50;

const SEVERITY_FILTERS = [
  { value: 'ALL',      label: 'All' },
  { value: 'INFO',     label: 'Info' },
  { value: 'WARNING',  label: 'Warning' },
  { value: 'CRITICAL', label: 'Critical' },
];

const SOURCE_SYSTEMS = [
  'FEENICS', 'LENEL', 'CCURE', 'BRIVO', 'OOSTO', 'WAVE',
  'GENETEC', 'MILESTONE', 'INTERCOM', 'RECORDING_ASSURANCE', 'CCM_INTERNAL',
];

const DATE_RANGES = [
  { value: 'all',    label: 'All Time' },
  { value: 'today',  label: 'Today' },
  { value: 'last7',  label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom' },
];

const OPEN_CASE_STATUSES = ['OPEN', 'IN_REVIEW'];

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

/** Resolve a date-range preset to { startDate, endDate } ISO strings. */
function resolveDateRange(range, customStart, customEnd) {
  const now = new Date();
  if (range === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { startDate: start.toISOString(), endDate: now.toISOString() };
  }
  if (range === 'last7' || range === 'last30') {
    const days = range === 'last7' ? 7 : 30;
    const start = new Date(now.getTime() - days * 86400000);
    return { startDate: start.toISOString(), endDate: now.toISOString() };
  }
  if (range === 'custom') {
    return {
      startDate: customStart ? new Date(customStart).toISOString() : null,
      endDate:   customEnd ? new Date(`${customEnd}T23:59:59`).toISOString() : null,
    };
  }
  return { startDate: null, endDate: null };
}

/** Relative time string, e.g. "3m ago". */
function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function systemLabel(s) {
  return String(s || '').replace(/_/g, ' ');
}

// ─────────────────────────────────────────────
// EVENTS TAB
// ─────────────────────────────────────────────

function EventsTab({ openCases, onCaseCreated }) {
  const [events, setEvents]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [severity, setSeverity]       = useState('ALL');
  const [sourceSystem, setSourceSystem] = useState('');
  const [dateRange, setDateRange]     = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');

  const [selectedEvent, setSelectedEvent] = useState(null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [search, severity, sourceSystem, dateRange, customStart, customEnd]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      if (severity !== 'ALL') params.set('severity', severity);
      if (sourceSystem) params.set('sourceSystem', sourceSystem);
      if (search) params.set('search', search);
      const { startDate, endDate } = resolveDateRange(dateRange, customStart, customEnd);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const r = await fetchWithTenant(`/api/investigations/events?${params.toString()}`);
      if (!r.ok) throw new Error('Failed to load events');
      const data = await r.json();
      setEvents(data.events || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      toast.error('Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [page, severity, sourceSystem, search, dateRange, customStart, customEnd]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  return (
    <>
      {/* ── FILTER BAR ── */}
      <div className="inv-filters">
        <div className="inv-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by actor name…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>

        <div className="inv-severity-chips">
          {SEVERITY_FILTERS.map(s => (
            <button
              key={s.value}
              className={`inv-chip ${severity === s.value ? 'active' : ''}`}
              onClick={() => setSeverity(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <select
          className="inv-select"
          value={sourceSystem}
          onChange={e => setSourceSystem(e.target.value)}
        >
          <option value="">All Systems</option>
          {SOURCE_SYSTEMS.map(s => (
            <option key={s} value={s}>{systemLabel(s)}</option>
          ))}
        </select>

        <select
          className="inv-select"
          value={dateRange}
          onChange={e => setDateRange(e.target.value)}
        >
          {DATE_RANGES.map(d => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>

        {dateRange === 'custom' && (
          <div className="inv-date-custom">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <span>–</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
          </div>
        )}
      </div>

      {/* ── EVENT LIST ── */}
      {loading ? (
        <div className="inv-list-loading">
          <Loader size={20} className="spin" />
          <span>Loading events…</span>
        </div>
      ) : events.length === 0 ? (
        <div className="inv-empty">
          <Inbox size={40} />
          <div className="inv-empty-title">No events found</div>
          <div className="inv-empty-sub">Try adjusting your filters or date range.</div>
        </div>
      ) : (
        <>
          <div className="inv-event-list">
            {events.map(ev => (
              <div
                key={ev.id}
                className="inv-event-row"
                onClick={() => setSelectedEvent(ev)}
              >
                <span className={`inv-sev-dot inv-sev-${ev.severity}`} title={ev.severity} />

                <div className="inv-event-left">
                  <span className="inv-event-type">{ev.eventTypeLabel}</span>
                  <span className="inv-event-device" title={ev.sourceDeviceLabel}>
                    {ev.sourceDeviceLabel}
                  </span>
                </div>

                <div className="inv-event-mid">
                  <div className="inv-event-actor">
                    <span className="inv-event-actor-name">{ev.actorName || 'Unknown'}</span>
                    <span className="inv-badge inv-badge-actor">{ev.actorType}</span>
                  </div>
                  <span
                    className="inv-event-time"
                    title={new Date(ev.eventTimestamp).toLocaleString()}
                  >
                    {timeAgo(ev.eventTimestamp)}
                  </span>
                </div>

                <div className="inv-event-right">
                  <span className="inv-badge inv-badge-system">{systemLabel(ev.sourceSystem)}</span>
                  <span className={`inv-badge inv-badge-status-${ev.correlationStatus}`}>
                    {ev.correlationStatus}
                  </span>
                  {ev.actionCount > 0 && (
                    <span className="inv-action-chip" title={`${ev.actionCount} action(s)`}>
                      <Zap size={10} /> {ev.actionCount}
                    </span>
                  )}
                  <button
                    className="btn-secondary sm"
                    onClick={e => { e.stopPropagation(); setSelectedEvent(ev); }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── PAGINATION ── */}
          <div className="inv-pagination">
            <button
              className="inv-page-btn"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="inv-pagination-info">
              Page {page} of {pages} · {total} event{total === 1 ? '' : 's'}
            </span>
            <button
              className="inv-page-btn"
              disabled={page >= pages}
              onClick={() => setPage(p => Math.min(pages, p + 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}

      {/* ── DETAIL DRAWER ── */}
      <EventDetailPane
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        openCases={openCases}
        onCaseCreated={onCaseCreated}
        onLinked={loadEvents}
      />
    </>
  );
}

// ─────────────────────────────────────────────
// CASES TAB
// ─────────────────────────────────────────────

function CasesTab({ cases, loading, onNewCase }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="inv-list-loading">
        <Loader size={20} className="spin" />
        <span>Loading cases…</span>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="inv-empty">
        <FolderOpen size={40} />
        <div className="inv-empty-title">No cases yet</div>
        <div className="inv-empty-sub">
          Open a case to group related events into an investigation.
        </div>
        <button className="btn-primary" onClick={onNewCase}>
          <Plus size={14} /> New Case
        </button>
      </div>
    );
  }

  return (
    <div className="inv-table-wrap">
      <table className="inv-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Evidence</th>
            <th>Items</th>
            <th>Incident</th>
            <th>Opened</th>
          </tr>
        </thead>
        <tbody>
          {cases.map(c => {
            const ev = c.evidenceStatus || 'PENDING';
            return (
              <tr key={c.id} onClick={() => navigate(`/investigations/cases/${c.id}`)}>
                <td className="inv-table-title">{c.title}</td>
                <td><span className={`inv-badge inv-badge-${c.status}`}>{c.status.replace('_', ' ')}</span></td>
                <td><span className={`inv-badge inv-badge-${c.priority}`}>{c.priority}</span></td>
                <td>
                  <span className={`inv-badge inv-badge-evidence-${ev}`}>
                    {ev === 'GATHERING' && <Loader size={11} className="spin" />}
                    {ev}
                  </span>
                </td>
                <td className="inv-table-muted">{c._count?.evidenceItems ?? 0}</td>
                <td className="inv-table-muted">
                  {c.incidentTimestamp ? new Date(c.incidentTimestamp).toLocaleString() : '—'}
                </td>
                <td className="inv-table-muted">{new Date(c.openedAt).toLocaleDateString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function Investigations() {
  const [tab, setTab]               = useState('events');
  const [cases, setCases]           = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const r = await fetchWithTenant('/api/investigations/cases?limit=200');
      if (!r.ok) throw new Error('Failed to load cases');
      const data = await r.json();
      setCases(data.cases || []);
    } catch {
      toast.error('Failed to load cases');
      setCases([]);
    } finally {
      setCasesLoading(false);
    }
  }, []);

  useEffect(() => { loadCases(); }, [loadCases]);

  // ── Tutorial wiring ──
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(investigationsTutorial);

  const openCases = cases.filter(c => OPEN_CASE_STATUSES.includes(c.status));

  return (
    <div className="inv-page">
      {/* ── PAGE HEADER ── */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Activity size={22} />
            Investigations
          </h1>
          <p className="page-subtitle">
            Browse correlation events and manage investigation cases.
          </p>
        </div>
        <div className="page-header-actions">
          {tab === 'cases' && (
            <button className="btn-primary sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} /> New Case
            </button>
          )}
          <button
            className="tutorial-help-btn"
            onClick={() => startTutorial(investigationsTutorial.module, investigationsTutorial.steps)}
            title="Take a tour"
            aria-label="Take a tour"
          >
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="inv-tabs">
        <button
          className={`inv-tab inv-tab-events ${tab === 'events' ? 'active' : ''}`}
          onClick={() => setTab('events')}
        >
          <Activity size={14} /> Events
        </button>
        <button
          className={`inv-tab inv-tab-cases ${tab === 'cases' ? 'active' : ''}`}
          onClick={() => setTab('cases')}
        >
          <FolderOpen size={14} /> Cases
          {cases.length > 0 && <span className="inv-tab-count">{cases.length}</span>}
        </button>
      </div>

      {/* ── TAB CONTENT ── */}
      {tab === 'events' ? (
        <EventsTab openCases={openCases} onCaseCreated={loadCases} />
      ) : (
        <CasesTab cases={cases} loading={casesLoading} onNewCase={() => setShowCreate(true)} />
      )}

      {/* ── NEW CASE MODAL (Cases tab) ── */}
      <CaseCreationWizard
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={loadCases}
      />

      {showPrompt && (
        <TutorialPrompt
          module={investigationsTutorial.module}
          moduleName={investigationsTutorial.moduleName}
          steps={investigationsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}
